import type { Settings } from './schemas/settings.js'

export type OvertimePolicy = Pick<
  Settings,
  | 'overtimePolicyCloseTime'
  | 'overtimePolicyBufferMinutes'
  | 'overtimePolicyMultiplier'
  | 'latePolicyWorkingDaysPerMonth'
  | 'latePolicyShiftMinutes'
>

export interface OvertimePay {
  /** Raw minutes after close time, before the buffer or the multiplier. */
  minutesOver: number
  /** minutesOver × multiplier — what actually gets paid for. */
  paidMinutes: number
  payMinor: number
}

function parseHHmm(value: string): number {
  const [h, m] = value.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

/**
 * What one overtime departure earns, per the workshop's configured policy.
 *
 * Returns null when there is nothing to pay: the departure is inside the
 * buffer (workers leaving before close time give a negative minutesOver,
 * which is <= the buffer too), or the worker has no monthly salary on file
 * to derive a rate from.
 *
 * The buffer is a cliff, not a discount — once minutesOver exceeds it, the
 * FULL time over close time is what gets multiplied, not just the part
 * past the buffer. Leaving 30 minutes late is 45 paid-minutes, not 30.
 */
export function computeOvertimePay(
  departureTime: string,
  monthlySalaryMinor: number | null,
  policy: OvertimePolicy,
): OvertimePay | null {
  const minutesOver = parseHHmm(departureTime) - parseHHmm(policy.overtimePolicyCloseTime)
  if (minutesOver <= policy.overtimePolicyBufferMinutes) return null
  if (!monthlySalaryMinor || monthlySalaryMinor <= 0) return null

  const paidMinutes = minutesOver * policy.overtimePolicyMultiplier
  const perMinuteRateMinor =
    monthlySalaryMinor / (policy.latePolicyWorkingDaysPerMonth * policy.latePolicyShiftMinutes)

  return {
    minutesOver,
    paidMinutes,
    payMinor: Math.round(paidMinutes * perMinuteRateMinor),
  }
}
